import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import {
  EVIDENCE,
  EVIDENCE_CAVEATS,
  SCALE_FACTS,
  ALGEBRA_OPERATIONS,
  CLINICAL_DISCLAIMER,
} from '../data/ewm';
import './Narrative.css';

function Evidence() {
  return (
    <div className="np-page">
      <SEO {...getSEOData('evidence')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Evidence</span>
          <h1>Six working scenarios — measured, not projected</h1>
          <p className="np-hero-sub">
            Every number below comes from a run in our own repositories: real model encodings, real
            documents, CPU-only hardware. The caveats travel with the claims.
          </p>
        </div>
      </section>

      {/* Scale facts */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2 np-h2--center">The bench</h2>
          <p className="np-lead np-lead--center">
            A side-car loop has been stood up on a vision-language-action stack, an OCR encoder,
            vision patch grids, a perceptron pyramid and a document platform — each one an
            integration exercise with a real vocabulary, real ids and a real decoder.
          </p>
          <div className="np-stats">
            {SCALE_FACTS.map((f) => (
              <div key={f.label} className="np-stat">
                <span className="np-stat-value">{f.value}</span>
                <span className="np-stat-label">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results table */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Measured results</h2>
          <p className="np-lead">
            Each row names the host, what was measured, the result, and the run it came from.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Scenario</th>
                  <th>Host</th>
                  <th>What was measured</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {EVIDENCE.map((e) => (
                  <tr key={e.id}>
                    <td><strong>{e.scenario}</strong></td>
                    <td>{e.host}</td>
                    <td>{e.measured}</td>
                    <td>{e.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="np-grid np-grid--3" style={{ marginTop: '30px' }}>
            {EVIDENCE.map((e) => (
              <div key={e.id} className="np-card">
                <span className="np-badge">{e.scenario}</span>
                <h3 className="np-h3" style={{ marginTop: '6px' }}>{e.result}</h3>
                <p className="np-body">{e.measured}</p>
                <p className="np-source">Source: {e.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the algebra answers */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">Operations on the state plane</h2>
          <p className="np-lead">
            Any ordered stream of encodings becomes a state you can measure. That is the whole
            claim — and it is modality-independent.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '220px' }}>Operation</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                {ALGEBRA_OPERATIONS.map((a) => (
                  <tr key={a.op}>
                    <td><strong>{a.op}</strong></td>
                    <td>{a.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="np-card" style={{ marginTop: '26px' }}>
            <h3 className="np-h3">The questions the algebra answers deterministically</h3>
            <ul className="np-list">
              <li>
                <strong>Novelty</strong> — the residual of a state against a frozen frame, or D/R/N
                across a commit.
              </li>
              <li>
                <strong>Similarity</strong> — BSS against any reference, or exact GF(2) coordinates
                when the state is in span.
              </li>
              <li>
                <strong>Coverage</strong> — the union invariant tells you nothing was lost, only
                redistributed.
              </li>
              <li>
                <strong>Reconstruction</strong> — ordered materialization returns the original
                sequence, or every candidate.
              </li>
            </ul>
            <p className="np-source" style={{ marginTop: '12px' }}>
              Semantics stay in the host&rsquo;s encoder and decoder. That separation is why the
              same side-car works for any model.
            </p>
          </div>
        </div>
      </section>

      {/* Caveats */}
      <section className="np-section np-section--alt">
        <div className="container">
          <div className="np-caveat">
            <h3 className="np-h3">Where the claim stops — the honest caveats</h3>
            <ul className="np-list">
              {EVIDENCE_CAVEATS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p className="np-body" style={{ marginTop: '14px' }}>
              We separate what is measured from what is planned — in this page and with our
              partners. That separation is deliberate: the side-car guarantees what it controls, and
              reports the rest.
            </p>
          </div>

          <div className="np-disclaimer" style={{ marginTop: '22px' }}>
            {CLINICAL_DISCLAIMER}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2 np-h2--center">Run the loop on your own model</h2>
          <p className="np-lead np-lead--center">
            The accumulated integration knowledge is the barrier — and the only way to test it is
            on your data, inside your own infrastructure.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/partners" className="np-btn np-btn--primary">Become a design partner</Link>
            <Link to="/technology" className="np-btn np-btn--secondary">How it works</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Evidence;
